import { Users, TrendingUp, Wallet } from '../../../icons';

import { KpiCard } from '../../../components/common/KpiCard';
import { formatCompact } from '../../../lib/format';
import { formatIncompleteNote, formatMoney, formatMoneyCompact } from '../../../lib/formatMoney';

/**
 * Tab-aggregated KPI strip rendered at the top of a roster tab.
 *
 * Used by:
 *   - Creators tab → /roster-kpis/creators (estimate)
 *   - Finalized tab → /roster-kpis/finalized (actuals — wires reach to
 *     actual_views and budget to locked_budget)
 *
 * Server-aggregated numbers — never depend on the page the user is on,
 * never shrink when a sub-filter is applied. Same Stripe / Linear /
 * GitHub headline-numbers pattern.
 *
 * Renders three cells: reach, budget, creator_count. Pass `loading`
 * while the BE call is in flight — the cells switch to skeleton blocks
 * without re-arranging the grid.
 */
export interface RosterKpiCardProps {
  reach: number;
  budget: number;
  creatorCount: number;
  /** Label for the creator-count cell. Defaults to "Creators" — pass
   *  "Live creators" / "Evaluating" / "Finalized" for tab-appropriate
   *  copy without forking the component. */
  creatorCountLabel?: string;
  /** Label for the views cell. Defaults to "Est. Views" — Finalized
   *  passes "Actual Views". */
  reachLabel?: string;
  /** Label for the budget cell. Defaults to "Est. Budget" — Finalized
   *  passes "Locked Budget". */
  budgetLabel?: string;
  /** Set when Go left the budget out because some foreign lines have no rate: the card shows the note instead of a number. */
  budgetIncompleteLines?: number;
  /** Hint under the budget when Go flags a foreign line inside it. Each tab words its own. */
  budgetRateNote?: string;
  loading?: boolean;
}

const COMPACT_FROM = 100_000;
const inr = (amount: number) => ({ amount, currency: 'INR' });

export function RosterKpiCard({
  reach,
  budget,
  creatorCount,
  creatorCountLabel = 'Creators',
  reachLabel = 'Est. Views',
  budgetLabel = 'Est. Budget',
  budgetIncompleteLines,
  budgetRateNote,
  loading = false,
}: RosterKpiCardProps) {
  const incomplete = budgetIncompleteLines !== undefined && budgetIncompleteLines > 0;
  const cells: Array<{ label: string; value: string; icon: typeof Users; valueTooltip?: string; hint?: string }> = [
    { label: creatorCountLabel, value: String(creatorCount), icon: Users },
    { label: reachLabel, value: reach > 0 ? formatCompact(reach) : '0', icon: TrendingUp },
    {
      label: budgetLabel,
      value: incomplete ? '—' : budget > 0 ? formatMoneyCompact(inr(budget)) : formatMoney(inr(0)),
      valueTooltip: !incomplete && budget >= COMPACT_FROM ? formatMoney(inr(budget)) : undefined,
      hint: incomplete ? formatIncompleteNote(budgetIncompleteLines ?? 0) : budgetRateNote,
      icon: Wallet,
    },
  ];

  // Uses the shared KpiCard (reporting General-Insights style) so the Creators /
  // Finalized strips match the reporting KPIs exactly.
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 lg:grid-cols-3">
      {cells.map((cell) => (
        <KpiCard
          key={cell.label}
          icon={cell.icon}
          label={cell.label}
          value={cell.value}
          valueTooltip={cell.valueTooltip}
          hint={cell.hint}
          loading={loading}
        />
      ))}
    </div>
  );
}
